import axios from "axios";
import { toast } from "react-toastify";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { replaceMultipleTokensFromAPI } from "../../assets/js/constants/commonFunctions";
import {
  addNewCategoryAPI,
  addNewDishItemAPI,
  addParcelOrderAPI,
  addRestauranTableAPI,
  applyDiscountAPI,
  cancelOrderAPI,
  changeAvailabilityDishItemAPI,
  changeUserStatusAPI,
  completeParcelOrderAPI,
  createStaffMemberAPI,
  deleteCategoryAPI,
  deleteDishItemAPI,
  deleteRestaurantTableAPI,
  deleteUserAPI,
  editCategoryAPI,
  getAllTablesAPI,
  getNewCategoriesAPI,
  getOrdersAPI,
  getParcelOrderAPI,
  getParcelsOrderAPI,
  getRestaurantByIdAPI,
  getTableOrderAPI,
  getUsersByRestaurantIdAPI,
  markOrderPaidAPI,
  menuByRestaurantIdAPI,
  placeTableOrderAPI,
  saveOrderAPI,
  updateDishItemAPI,
  updateDishStatusAPI,
  updateOrderAPI,
  updateRestauranTableAPI,
  updateRestaurantAPI,
  updateUserAPI,
} from "../../assets/js/constants/APIURLs";
import {
  ERRORSTATUS,
  existingCategory,
  FAILEDSTATUS,
  IDLESTATUS,
  LOADINGSTATUS,
  secureSecretkey,
  SUCCESSSTATUS,
} from "../../assets/js/constants/constant";

// 1. Create Restaurant Staff Member
export const createStaffMember = createAsyncThunk(
  "admin/createUser",
  async ({ values, restaurantId }, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(createStaffMemberAPI, { 1: restaurantId }),
        {
          loggedInUserId,
          name: values.name,
          phoneNumber: values.phoneNumber,
          email: values.email,
          password: values.password,
          role: values.role,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message || "User created successfully");
      return res.data.user;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Something went wrong";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// 2. Get users by restaurantId
export const getUsersByRestaurantId = createAsyncThunk(
  "user/getUsersByRestaurantId",
  async (restaurantId, { rejectWithValue }) => {
    const loggedInUserId = localStorage.getItem("userId");
    try {
      const res = await axios.post(
        replaceMultipleTokensFromAPI(getUsersByRestaurantIdAPI, {
          1: restaurantId,
        }),
        {
          loggedInUserId,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

// 3. Change User Status - Activating and Deactivating user
export const changeUserStatus = createAsyncThunk(
  "user/changeUserStatusByUser",
  async (userId, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(changeUserStatusAPI, {
          1: userId,
        }),
        {
          loggedInUserId,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data.updatedUser;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

// 4. Update user
export const updateUser = createAsyncThunk(
  "user/updateUser",
  async ({ userId, updatedData }, { rejectWithValue }) => {
    const loggedInUserId = localStorage.getItem("userId");
    try {
      const res = await axios.put(
        replaceMultipleTokensFromAPI(updateUserAPI, { 1: userId }),
        {
          loggedInUserId,
          updatedData,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data.updatedUser;
    } catch (err) {
      toast.error(err.response.data.error);
      return rejectWithValue(err.response.data);
    }
  },
);

// 5. Delete user
export const deleteUser = createAsyncThunk(
  "user/deleteUser",
  async (userId, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.delete(
        replaceMultipleTokensFromAPI(deleteUserAPI, { 1: userId }),
        {
          data: {
            loggedInUserId,
          },
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return { deleteUserId: userId };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

// 6. Get restaurant by Id
export const getRestaurantById = createAsyncThunk(
  "user/getRestaurant",
  async ({ userId, restaurantId }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        replaceMultipleTokensFromAPI(getRestaurantByIdAPI, { 1: restaurantId }),
        {
          loggedInUserId: userId,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data;
    } catch (err) {
      toast.error(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

// 7. Get New Category
export const getNewCategories = createAsyncThunk(
  "user/getNewCategories",
  async (restaurantId, { rejectWithValue }) => {
    const loggedInUserId = localStorage.getItem("userId");
    try {
      const res = await axios.post(
        replaceMultipleTokensFromAPI(getNewCategoriesAPI, { 1: restaurantId }),
        {
          loggedInUserId,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      return res.data.categories;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

// 8. Add New Category
export const addNewCategory = createAsyncThunk(
  "user/addNewCategory",
  async ({ restaurantId, values }, { rejectWithValue }) => {
    const loggedInUserId = localStorage.getItem("userId");
    try {
      const isDuplicate = existingCategory.some(
        (cat) =>
          cat.categoryName.trim().toLowerCase() ===
          values.categoryName.trim().toLowerCase(),
      );
      if (isDuplicate) {
        toast.error("Category already exists");
        return rejectWithValue("Category already exists");
      }
      const res = await axios.post(
        replaceMultipleTokensFromAPI(addNewCategoryAPI, { 1: restaurantId }),
        {
          loggedInUserId,
          categoryName: values.categoryName,
          categoryPhotoURL: values.categoryPhotoURL,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data.categories;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

// 9. Edit Category
export const editCategory = createAsyncThunk(
  "user/editCategory",
  async ({ restaurantId, categoryId, updatedData }, { rejectWithValue }) => {
    const loggedInUserId = localStorage.getItem("userId");
    try {
      const isDuplicate = existingCategory.some(
        (cat) =>
          cat.categoryName.trim().toLowerCase() ===
          updatedData.categoryName.trim().toLowerCase(),
      );
      if (isDuplicate) {
        toast.error("Category already exists");
        return rejectWithValue("Category already exists");
      }
      const res = await axios.put(
        replaceMultipleTokensFromAPI(editCategoryAPI, {
          1: restaurantId,
          2: categoryId,
        }),
        {
          loggedInUserId,
          updatedData,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data.categories;
    } catch (err) {
      toast.error(err.response.data.error);
      return rejectWithValue(err.response.data);
    }
  },
);

// 10. Delete Category
export const deleteCategory = createAsyncThunk(
  "user/deleteCategory",
  async ({ restaurantId, categoryId }, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.delete(
        replaceMultipleTokensFromAPI(deleteCategoryAPI, {
          1: restaurantId,
          2: categoryId,
        }),
        {
          data: {
            loggedInUserId,
          },
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return { deleteCategoryId: categoryId };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

// 11. Update restaurant
export const updateRestaurant = createAsyncThunk(
  "user/updateRestaurant",
  async ({ restaurantId, updatedData }, { rejectWithValue }) => {
    const loggedInUserId = localStorage.getItem("userId");
    try {
      const res = await axios.put(
        replaceMultipleTokensFromAPI(updateRestaurantAPI, {
          1: restaurantId,
        }),
        {
          loggedInUserId,
          updatedData,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data.categories;
    } catch (err) {
      toast.error(err.response.data.error);
      return rejectWithValue(err.response.data);
    }
  },
);

// 12. Get menu by restaurantId
export const getMenuByRestaurantId = createAsyncThunk(
  "user/getMenuByRestaurantId",
  async ({ restaurantId, userId }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        replaceMultipleTokensFromAPI(menuByRestaurantIdAPI, {
          1: restaurantId,
        }),
        {
          loggedInUserId: userId,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data;
    } catch (err) {
      toast.error(err?.response?.data || "Failed to fetch menu");
      return rejectWithValue(err?.response?.data);
    }
  },
);

// 13. Add menu dish item
export const addNewDishItem = createAsyncThunk(
  "menu/addNewDishItem",
  async ({ restaurantId, values }, { rejectWithValue }) => {
    const loggedInUserId = localStorage.getItem("userId");
    try {
      const res = await axios.post(
        replaceMultipleTokensFromAPI(addNewDishItemAPI, { 1: restaurantId }),
        {
          loggedInUserId,
          ...values,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data.data;
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
      return rejectWithValue(err.response?.data);
    }
  },
);

// 14. Change availability menu dish item
export const changeAvailabilityDishItem = createAsyncThunk(
  "menu/changeAvailabilityDishItem",
  async ({ restaurantId, dishId, available }, { rejectWithValue }) => {
    const loggedInUserId = localStorage.getItem("userId");
    try {
      const res = await axios.post(
        replaceMultipleTokensFromAPI(changeAvailabilityDishItemAPI, {
          1: restaurantId,
          2: dishId,
        }),
        {
          loggedInUserId,
          available,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return { dishId, available };
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
      return rejectWithValue(err.response?.data);
    }
  },
);

// 15. Update menu dish item
export const updateDishItem = createAsyncThunk(
  "menu/updateDishItem",
  async ({ restaurantId, dishId, updatedData }, { rejectWithValue }) => {
    const loggedInUserId = localStorage.getItem("userId");
    try {
      const res = await axios.put(
        replaceMultipleTokensFromAPI(updateDishItemAPI, {
          1: restaurantId,
          2: dishId,
        }),
        {
          loggedInUserId,
          ...updatedData,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return { dishId, updatedData };
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
      return rejectWithValue(err.response?.data);
    }
  },
);

// 16. Delete availability menu dish item
export const deleteDishItem = createAsyncThunk(
  "menu/deleteDishItem",
  async ({ restaurantId, dishId }, { rejectWithValue }) => {
    const loggedInUserId = localStorage.getItem("userId");

    try {
      const res = await axios.delete(
        replaceMultipleTokensFromAPI(deleteDishItemAPI, {
          1: restaurantId,
          2: dishId,
        }),
        {
          data: {
            loggedInUserId,
          },
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );

      toast.success(res.data.message);

      return { dishId };
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
      return rejectWithValue(err.response?.data);
    }
  },
);

// 17. Add Parcel Order
export const addParcelOrder = createAsyncThunk(
  "user/addParcelOrder",
  async ({ restaurantId, data }, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(addParcelOrderAPI, {
          1: restaurantId,
        }),
        {
          loggedInUserId,
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data.parcel;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Something went wrong";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// 18. Apply Discount
export const applyDiscount = createAsyncThunk(
  "user/applyDiscount",
  async (
    { restaurantId, billId, discountType, discountValue },
    { rejectWithValue },
  ) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(applyDiscountAPI, {
          1: restaurantId,
        }),
        {
          loggedInUserId,
          billId,
          discountType: discountType,
          discountValue: Number(discountValue),
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Something went wrong";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// 19. Update Order
export const updateOrder = createAsyncThunk(
  "user/updateOrder",
  async ({ restaurantId, billId, data }, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.put(
        replaceMultipleTokensFromAPI(updateOrderAPI, {
          1: restaurantId,
        }),
        {
          loggedInUserId,
          billId,
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );

      toast.success(res.data.message);

      return {
        updatedOrder: res.data.data,
        orderType: res.data.orderType,
      };
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Something went wrong";

      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// 20. Mark Order Paid
export const markOrderPaid = createAsyncThunk(
  "user/markOrderPaid",
  async ({ restaurantId, billId, modeOfPayment }, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.put(
        replaceMultipleTokensFromAPI(markOrderPaidAPI, {
          1: restaurantId,
        }),
        {
          loggedInUserId,
          billId,
          modeOfPayment,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Something went wrong";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// 21. Complete Parcel Order
export const completeParcelOrder = createAsyncThunk(
  "user/completeParcelOrder",
  async ({ restaurantId, billId }, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(completeParcelOrderAPI, {
          1: restaurantId,
        }),
        {
          loggedInUserId,
          billId,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return { billId };
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Something went wrong";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// 22. Cancel Order
export const cancelOrder = createAsyncThunk(
  "user/cancelOrder",
  async ({ restaurantId, billId }, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.delete(
        replaceMultipleTokensFromAPI(cancelOrderAPI, {
          1: restaurantId,
        }),
        {
          data: { loggedInUserId, billId },
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return {
        billId: res.data.billId,
        orderType: res.data.orderType,
      };
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Something went wrong";

      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// 23. Place Order for Table
export const placeTableOrder = createAsyncThunk(
  "user/placeTableOrder",
  async ({ restaurantId, tableID, data }, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(placeTableOrderAPI, {
          1: restaurantId,
          2: tableID,
        }),
        {
          loggedInUserId,
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Something went wrong";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// 24. Fetch Single Table Order Details
export const fetchTableOrder = createAsyncThunk(
  "user/fetchTableOrder",
  async ({ restaurantId, tableID }, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(getTableOrderAPI, {
          1: restaurantId,
          2: tableID,
        }),
        {
          loggedInUserId,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      return res.data;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Failed to fetch table order";
      return rejectWithValue(errorMessage);
    }
  },
);

// 24. Fetch Single Parcel Order Details
export const fetchParcelOrder = createAsyncThunk(
  "user/fetchParcelOrder",
  async ({ restaurantId, billId }, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(getParcelOrderAPI, {
          1: restaurantId,
          2: billId,
        }),
        {
          loggedInUserId,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      return res.data;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Failed to fetch table order";
      return rejectWithValue(errorMessage);
    }
  },
);

// 25. Change Dish Status
export const changeDishStatus = createAsyncThunk(
  "user/changeDishStatus",
  async (
    { restaurantId, billId, dishId, orderItemId, orderType, changeStatus },
    { rejectWithValue },
  ) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.put(
        replaceMultipleTokensFromAPI(updateDishStatusAPI, {
          1: restaurantId,
        }),
        {
          loggedInUserId,
          billId,
          dishId,
          orderItemId,
          orderType,
          changeStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return {
        message: res.data.message,
        dishId,
        billId,
        orderItemId,
        orderType,
        status: changeStatus, // map changeStatus → status
      };
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Something went wrong";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// 26. Add new restaurant table
export const addTable = createAsyncThunk(
  "restaurant/addTable",
  async ({ restaurantId, values }, { rejectWithValue }) => {
    const loggedInUserId = localStorage.getItem("userId");
    try {
      const res = await axios.post(
        replaceMultipleTokensFromAPI(addRestauranTableAPI, {
          1: restaurantId,
        }),
        {
          loggedInUserId,
          ...values,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data.table;
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
      return rejectWithValue(err.response?.data);
    }
  },
);

// 27. Update restaurant table
export const updateTable = createAsyncThunk(
  "restaurant/updateTable",
  async ({ restaurantId, tableID, updatedData }, { rejectWithValue }) => {
    const loggedInUserId = localStorage.getItem("userId");
    try {
      const res = await axios.put(
        replaceMultipleTokensFromAPI(updateRestauranTableAPI, {
          1: restaurantId,
          2: tableID,
        }),
        {
          loggedInUserId,
          updatedData,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return { tableID, updatedData };
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
      return rejectWithValue(err.response?.data);
    }
  },
);

// 28. Delete availability menu dish item
export const deleteTable = createAsyncThunk(
  "restaurant/deleteTable",
  async ({ restaurantId, tableID }, { rejectWithValue }) => {
    const loggedInUserId = localStorage.getItem("userId");
    try {
      const res = await axios.delete(
        replaceMultipleTokensFromAPI(deleteRestaurantTableAPI, {
          1: restaurantId,
          2: tableID,
        }),
        {
          data: { loggedInUserId },
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return { tableID };
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
      return rejectWithValue(err.response?.data);
    }
  },
);

// 29. Fetch tables
export const fetchAllTables = createAsyncThunk(
  "restaurant/fetchAllTables",
  async ({ restaurantId }, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(getAllTablesAPI, {
          1: restaurantId,
        }),
        {
          loggedInUserId,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      return res.data;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Failed to fetch tables details";
      return rejectWithValue(errorMessage);
    }
  },
);

// 30. Fetch parcel orders
export const fetchParcelsOrder = createAsyncThunk(
  "restaurant/fetchParcelsOrder",
  async ({ restaurantId }, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(getParcelsOrderAPI, {
          1: restaurantId,
        }),
        {
          loggedInUserId,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      return res.data;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Failed to fetch parcel orders";
      return rejectWithValue(errorMessage);
    }
  },
);

// 31. Save Order
export const saveOrder = createAsyncThunk(
  "user/saveOrder",
  async (
    { restaurantId, billId, NumberOfItems, amountPaid, modeOfPayment },
    { rejectWithValue },
  ) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(saveOrderAPI, {
          1: restaurantId,
        }),
        {
          loggedInUserId,
          restaurantId,
          billId,
          NumberOfItems,
          amountPaid,
          modeOfPayment,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      return res.data.data;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Something went wrong";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// 32. Fetch Orders
export const fetchOrders = createAsyncThunk(
  "user/fetchOrders",
  async ({ restaurantId }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        replaceMultipleTokensFromAPI(getOrdersAPI, {
          1: restaurantId,
        }),
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      return res.data.data;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Something went wrong";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

const userSlice = createSlice({
  name: "user",
  initialState: {
    user: null,
    restaurant: null,
    menu: null,
    restaurantStaffs: [],
    newCategory: [],
    tableOrder: {},
    parcelOrder: {},
    ordersHistory: [],
    status: IDLESTATUS,
    createStaffMemberStatus: IDLESTATUS,
    updateUserStatus: IDLESTATUS,
    categoryStatus: IDLESTATUS,
    dishStatus: IDLESTATUS,
    tableStatus: IDLESTATUS,
    placeOrderStatus: IDLESTATUS,
    updateOrderStatus: IDLESTATUS,
    markPaidStatus: IDLESTATUS,
    cancelOrderStatus: IDLESTATUS,
    discountStatus: IDLESTATUS,
    fetchTableOrderStatus: IDLESTATUS,
    fetchParcelOrderStatus: IDLESTATUS,
    changeOrderDishStatus: IDLESTATUS,
    fetchAllTablesStatus: IDLESTATUS,
    fetchParcelsOrdersStatus: IDLESTATUS,
    ordersHistoryStatus: IDLESTATUS,
    error: null,
    dishError: null,
    categoryError: null,
    createStaffMemberError: null,
  },
  reducers: {
    clearCategoryState: (state) => {
      state.categoryStatus = IDLESTATUS;
      state.categoryError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // CREATE USER
      .addCase(createStaffMember.pending, (state) => {
        state.createStaffMemberStatus = LOADINGSTATUS;
        state.createStaffMemberError = null;
      })
      .addCase(createStaffMember.fulfilled, (state, action) => {
        state.createStaffMemberStatus = SUCCESSSTATUS;
        if (action.payload) {
          state.restaurantStaffs.push(action.payload);
        }
      })
      .addCase(createStaffMember.rejected, (state, action) => {
        state.createStaffMemberStatus = FAILEDSTATUS;
        state.createStaffMemberError = action.payload;
      })

      // RESTAURANT USER
      .addCase(getUsersByRestaurantId.pending, (state) => {
        state.status = LOADINGSTATUS;
      })
      .addCase(getUsersByRestaurantId.fulfilled, (state, action) => {
        state.status = SUCCESSSTATUS;
        state.restaurantStaffs = action.payload.staffsData || [];
      })
      .addCase(getUsersByRestaurantId.rejected, (state, action) => {
        state.status = ERRORSTATUS;
        state.error = action.payload;
      })

      // ACTIVATE AND DEACTIVATE USER
      .addCase(changeUserStatus.pending, (state) => {
        state.status = LOADINGSTATUS;
      })
      .addCase(changeUserStatus.fulfilled, (state, action) => {
        state.status = SUCCESSSTATUS;
        const updatedUser = action.payload;
        const index = state.restaurantStaffs.findIndex(
          (user) => user.userId === updatedUser.userId,
        );
        if (index !== -1) {
          state.restaurantStaffs[index] = {
            ...state.restaurantStaffs[index],
            activated: updatedUser.activated,
            modifiedAt: updatedUser.modifiedAt,
          };
        }
      })
      .addCase(changeUserStatus.rejected, (state, action) => {
        state.status = FAILEDSTATUS;
        state.error = action.payload;
      })

      // UPDATE USER
      .addCase(updateUser.pending, (state) => {
        state.updateUserStatus = LOADINGSTATUS;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.updateUserStatus = SUCCESSSTATUS;
        const updatedUser = action.payload;
        state.restaurantStaffs = state.restaurantStaffs.map((user) =>
          user.userId === updatedUser.userId ? updatedUser : user,
        );
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.updateUserStatus = FAILEDSTATUS;
        state.error = action.payload;
      })

      // DELETE USER
      .addCase(deleteUser.pending, (state) => {
        state.deleteUserStatus = LOADINGSTATUS;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.deleteUserStatus = SUCCESSSTATUS;
        const { deleteUserId } = action.payload;
        state.restaurantStaffs = state.restaurantStaffs.filter(
          (user) => user.userId !== deleteUserId,
        );
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deleteUserStatus = FAILEDSTATUS;
        state.error = action.payload;
      })

      // GET RESTAURANT
      .addCase(getRestaurantById.pending, (state) => {
        state.status = LOADINGSTATUS;
      })
      .addCase(getRestaurantById.fulfilled, (state, action) => {
        state.status = SUCCESSSTATUS;
        state.restaurant = action.payload;
      })
      .addCase(getRestaurantById.rejected, (state, action) => {
        state.status = FAILEDSTATUS;
        state.error = action.payload;
      })

      // GET NEW CATEGORY
      .addCase(getNewCategories.pending, (state) => {
        state.categoryStatus = LOADINGSTATUS;
        state.categoryError = null;
      })
      .addCase(getNewCategories.fulfilled, (state, action) => {
        state.categoryStatus = SUCCESSSTATUS;
        const categories = action.payload;
        if (!state.newCategory) {
          state.newCategory = [];
        }
        state.newCategory = categories;
      })
      .addCase(getNewCategories.rejected, (state, action) => {
        state.categoryStatus = FAILEDSTATUS;
        state.categoryError = action.payload.message;
      })

      // ADD NEW CATEGORY
      .addCase(addNewCategory.pending, (state) => {
        state.categoryStatus = LOADINGSTATUS;
        state.categoryError = null;
      })
      .addCase(addNewCategory.fulfilled, (state, action) => {
        state.categoryStatus = SUCCESSSTATUS;
        const categories = action.payload;
        if (!state.newCategory) {
          state.newCategory = [];
        }
        state.newCategory = categories;
      })
      .addCase(addNewCategory.rejected, (state, action) => {
        state.categoryStatus = FAILEDSTATUS;
        state.categoryError = action.payload.message;
      })

      // EDIT CATEGORY
      .addCase(editCategory.pending, (state) => {
        state.categoryStatus = LOADINGSTATUS;
        state.categoryError = null;
      })
      .addCase(editCategory.fulfilled, (state, action) => {
        state.categoryStatus = SUCCESSSTATUS;
        const updatedCategories = action.payload;
        state.newCategory = updatedCategories;
      })
      .addCase(editCategory.rejected, (state, action) => {
        state.categoryStatus = FAILEDSTATUS;
        state.categoryError = action.payload?.message;
      })

      // DELETE CATEGORY
      .addCase(deleteCategory.pending, (state) => {
        state.categoryStatus = LOADINGSTATUS;
        state.categoryError = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categoryStatus = SUCCESSSTATUS;
        const { deleteCategoryId } = action.payload;
        state.newCategory = state.newCategory?.filter(
          (cat) => cat.categoryId !== deleteCategoryId,
        );
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.categoryStatus = FAILEDSTATUS;
        state.categoryError = action.payload?.message;
      })

      // UPDATE RESTAURANT
      .addCase(updateRestaurant.pending, (state) => {
        state.status = LOADINGSTATUS;
      })
      .addCase(updateRestaurant.fulfilled, (state, action) => {
        state.status = IDLESTATUS;
        state.restaurant = action.payload;
      })
      .addCase(updateRestaurant.rejected, (state, action) => {
        state.status = FAILEDSTATUS;
        state.error = action.payload;
      })

      // GET MENU BY RESTAURANT ID
      .addCase(getMenuByRestaurantId.pending, (state) => {
        state.status = LOADINGSTATUS;
      })
      .addCase(getMenuByRestaurantId.fulfilled, (state, action) => {
        state.status = SUCCESSSTATUS;
        state.menu = action.payload;
      })
      .addCase(getMenuByRestaurantId.rejected, (state, action) => {
        state.status = FAILEDSTATUS;
        state.error = action.payload;
      })

      // ADD NEW DISH ITEM
      .addCase(addNewDishItem.pending, (state) => {
        state.dishStatus = LOADINGSTATUS;
      })
      .addCase(addNewDishItem.fulfilled, (state, action) => {
        state.dishStatus = SUCCESSSTATUS;
        state.menu.dishItems.push(action.payload);
      })
      .addCase(addNewDishItem.rejected, (state, action) => {
        state.dishStatus = FAILEDSTATUS;
        state.dishError = action.payload;
      })

      // CHANGE AVAILABILITY NEW DISH ITEM
      .addCase(changeAvailabilityDishItem.pending, (state) => {
        state.dishStatus = LOADINGSTATUS;
      })
      .addCase(changeAvailabilityDishItem.fulfilled, (state, action) => {
        state.dishStatus = SUCCESSSTATUS;
        const item = state.menu.dishItems.find(
          (d) => d.dishId === action.payload.dishId,
        );
        if (item) item.available = action.payload.available;
      })
      .addCase(changeAvailabilityDishItem.rejected, (state, action) => {
        state.dishStatus = FAILEDSTATUS;
        state.dishError = action.payload;
      })

      // UPDATE DISH ITEM
      .addCase(updateDishItem.pending, (state) => {
        state.dishStatus = LOADINGSTATUS;
      })
      .addCase(updateDishItem.fulfilled, (state, action) => {
        state.dishStatus = SUCCESSSTATUS;
        const index = state.menu.dishItems.findIndex(
          (d) => d.dishId === action.payload.dishId,
        );
        if (index !== -1) {
          state.menu.dishItems[index] = {
            ...state.menu.dishItems[index],
            ...action.payload.updatedData,
          };
        }
      })
      .addCase(updateDishItem.rejected, (state, action) => {
        state.dishStatus = FAILEDSTATUS;
        state.dishError = action.payload;
      })

      // DELETE NEW DISH ITEM
      .addCase(deleteDishItem.pending, (state) => {
        state.dishStatus = LOADINGSTATUS;
      })
      .addCase(deleteDishItem.fulfilled, (state, action) => {
        state.dishStatus = SUCCESSSTATUS;
        state.menu.dishItems = state.menu.dishItems.filter(
          (d) => d.dishId !== action.payload.dishId,
        );
      })
      .addCase(deleteDishItem.rejected, (state, action) => {
        state.dishStatus = FAILEDSTATUS;
        state.dishError = action.payload;
      })

      // PLACE PARCEL ORDER
      .addCase(addParcelOrder.pending, (state) => {
        state.placeOrderStatus = LOADINGSTATUS;
      })
      .addCase(addParcelOrder.fulfilled, (state, action) => {
        if (!state.restaurant.parcelsOrder) {
          state.restaurant.parcelsOrder = [];
        }
        state.restaurant.parcelsOrder.unshift(action.payload);
        state.placeOrderStatus = SUCCESSSTATUS;
      })
      .addCase(addParcelOrder.rejected, (state, action) => {
        state.placeOrderStatus = FAILEDSTATUS;
        state.error = action.payload;
      })

      .addCase(applyDiscount.pending, (state) => {
        state.discountStatus = LOADINGSTATUS;
      })
      .addCase(applyDiscount.fulfilled, (state, action) => {
        state.discountStatus = SUCCESSSTATUS;
        const { billId, discountType, discountValue } = action.meta.arg;
        const orderIndex = state.restaurant.parcelsOrder?.findIndex(
          (parcel) => parcel?.orderDetails?.data?.billId === billId,
        );
        if (orderIndex !== -1) {
          state.restaurant.parcelsOrder[orderIndex].orderDetails.data.discount =
            {
              discountType: discountType,
              dicountValue: Number(discountValue),
            };
        }
      })
      .addCase(applyDiscount.rejected, (state, action) => {
        state.discountStatus = FAILEDSTATUS;
        state.error = action.payload;
      })

      // UPDATE ORDER (COMMON FOR PARCEL AND TABLE)
      .addCase(updateOrder.pending, (state) => {
        state.updateOrderStatus = LOADINGSTATUS;
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        const { updatedOrder, orderType } = action.payload;
        if (orderType === "table") {
          const index = state.restaurant.tables?.findIndex(
            (o) => o?.data?.billId === updatedOrder?.data?.billId,
          );
          if (index !== -1) {
            state.restaurant.tableOrders[index] = updatedOrder;
          }
        }
        if (orderType === "parcel") {
          const index = state.restaurant.parcelsOrder?.findIndex(
            (o) => o?.data?.billId === updatedOrder?.data?.billId,
          );
          if (index !== -1) {
            state.restaurant.parcelsOrder[index] = updatedOrder;
          }
        }
        state.updateOrderStatus = SUCCESSSTATUS;
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.updateOrderStatus = FAILEDSTATUS;
        state.error = action.payload;
      })

      // MARK ORDER PAID
      .addCase(markOrderPaid.pending, (state) => {
        state.markPaidStatus = "loading";
      })
      .addCase(markOrderPaid.fulfilled, (state, action) => {
        state.markPaidStatus = "succeeded";
        const { billId, orderType, modeOfPayment } = action.payload;
        if (orderType === "parcel") {
          state.restaurant.parcelsOrder = state.restaurant.parcelsOrder.map(
            (parcel) => {
              if (parcel?.orderDetails?.data?.billId === billId) {
                return {
                  ...parcel,
                  orderDetails: {
                    ...parcel.orderDetails,
                    data: {
                      ...parcel.orderDetails.data,
                      modeOfPayment,
                    },
                  },
                };
              }
              return parcel;
            },
          );
        }
        if (orderType === "table") {
          state.restaurant.tables = state.restaurant.tables.map((table) => {
            if (table?.currentOrderDetails?.data?.billId === billId) {
              return {
                ...table,
                currentOrderDetails: {},
              };
            }
            return table;
          });
        }
      })
      .addCase(markOrderPaid.rejected, (state) => {
        state.markPaidStatus = "failed";
      })

      // COMPLETE PARCEL ORDER
      .addCase(completeParcelOrder.pending, (state) => {
        state.completeParcelStatus = "loading";
      })
      .addCase(completeParcelOrder.fulfilled, (state, action) => {
        state.completeParcelStatus = "succeeded";
        const { billId } = action.payload;
        state.restaurant.parcelsOrder = state.restaurant.parcelsOrder.filter(
          (parcel) => parcel?.orderDetails?.data?.billId !== billId,
        );
      })
      .addCase(completeParcelOrder.rejected, (state) => {
        state.completeParcelStatus = "failed";
      })

      // CANCEL ORDER
      .addCase(cancelOrder.pending, (state) => {
        state.cancelOrderStatus = LOADINGSTATUS;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        const { billId, orderType } = action.payload;
        if (orderType === "parcel") {
          state.restaurant.parcelsOrder = state.restaurant.parcelsOrder.filter(
            (order) => order.orderDetails.data.billId !== billId,
          );
        }
        if (orderType === "table") {
          state.restaurant.tables = state.restaurant.tables.map((table) => {
            if (table?.currentOrderDetails?.data?.billId === billId) {
              return {
                ...table,
                currentOrderDetails: {},
              };
            }
            return table;
          });
        }
        state.cancelOrderStatus = SUCCESSSTATUS;
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.cancelOrderStatus = FAILEDSTATUS;
        state.error = action.payload;
      })

      // PLACE TABLE ORDER
      .addCase(placeTableOrder.pending, (state) => {
        state.placeOrderStatus = "loading";
      })
      .addCase(placeTableOrder.fulfilled, (state, action) => {
        state.placeOrderStatus = "succeeded";
        const { tableID, data } = action.payload;
        state.restaurant.tables = state.restaurant.tables.map((table) => {
          if (table.tableID === tableID) {
            return {
              ...table,
              currentOrderDetails: data,
            };
          }
          return table;
        });
      })
      .addCase(placeTableOrder.rejected, (state) => {
        state.placeOrderStatus = "failed";
      })

      // ADD TABLE
      .addCase(addTable.pending, (state) => {
        state.tableStatus = LOADINGSTATUS;
      })
      .addCase(addTable.fulfilled, (state, action) => {
        state.tableStatus = SUCCESSSTATUS;
        state.restaurant.tables.push(action.payload);
      })
      .addCase(addTable.rejected, (state, action) => {
        state.tableStatus = FAILEDSTATUS;
        state.error = action.payload;
      })

      // FETCH TABLE ORDER DETAILS
      .addCase(fetchTableOrder.pending, (state) => {
        state.fetchTableOrderStatus = LOADINGSTATUS;
      })
      .addCase(fetchTableOrder.fulfilled, (state, action) => {
        state.fetchTableOrderStatus = SUCCESSSTATUS;
        state.tableOrder = action.payload.data;
      })
      .addCase(fetchTableOrder.rejected, (state) => {
        state.fetchTableOrderStatus = FAILEDSTATUS;
      })

      // FETCHED PARCEL ORDER
      .addCase(fetchParcelOrder.pending, (state) => {
        state.fetchParcelOrderStatus = LOADINGSTATUS;
      })
      .addCase(fetchParcelOrder.fulfilled, (state, action) => {
        state.fetchParcelOrderStatus = SUCCESSSTATUS;
        state.parcelOrder = action.payload.data;
      })
      .addCase(fetchParcelOrder.rejected, (state) => {
        state.fetchParcelOrderStatus = FAILEDSTATUS;
      })

      // UPDATED ORDERED DISH STATUS
      .addCase(changeDishStatus.pending, (state) => {
        state.changeOrderDishStatus = LOADINGSTATUS;
      })
      .addCase(changeDishStatus.fulfilled, (state, action) => {
        state.changeOrderDishStatus = SUCCESSSTATUS;
        const { dishId, orderItemId, billId, status, orderType } =
          action.payload;
        const matchItem = (item) =>
          orderItemId
            ? item.orderItemId === orderItemId
            : item.dishId === dishId;
        if (orderType === "Table") {
          state.restaurant?.tables?.forEach((table) => {
            const order = table.currentOrderDetails?.data;
            if (order?.billId === billId) {
              order.items?.forEach((item) => {
                if (matchItem(item)) {
                  item.status = status;
                }
              });
            }
          });
        }
        if (orderType === "Parcel") {
          state.restaurant?.parcelsOrder?.forEach((parcel) => {
            const order = parcel.orderDetails?.data;
            if (order?.billId === billId) {
              order.items?.forEach((item) => {
                if (matchItem(item)) {
                  item.status = status;
                }
              });
            }
          });
        }
      })
      .addCase(changeDishStatus.rejected, (state) => {
        state.changeOrderDishStatus = "error";
      })

      // UPDATE TABLE
      .addCase(updateTable.pending, (state) => {
        state.tableStatus = LOADINGSTATUS;
      })
      .addCase(updateTable.fulfilled, (state, action) => {
        const { tableID, updatedData } = action.payload;
        const table = state.restaurant.tables.find(
          (t) => t.tableID === tableID,
        );
        if (table) {
          Object.assign(table, updatedData);
        }
      })
      .addCase(updateTable.rejected, (state, action) => {
        state.tableStatus = FAILEDSTATUS;
        state.error = action.payload;
      })

      // DELETE TABLE
      .addCase(deleteTable.pending, (state) => {
        state.tableStatus = LOADINGSTATUS;
      })
      .addCase(deleteTable.fulfilled, (state, action) => {
        const { tableID } = action.payload;
        state.restaurant.tables = state.restaurant.tables.filter(
          (t) => t.tableID !== tableID,
        );
      })
      .addCase(deleteTable.rejected, (state, action) => {
        state.tableStatus = FAILEDSTATUS;
        state.error = action.payload;
      })

      // FETCHED TABLES
      .addCase(fetchAllTables.pending, (state) => {
        state.fetchAllTablesStatus = LOADINGSTATUS;
      })
      .addCase(fetchAllTables.fulfilled, (state, action) => {
        state.fetchAllTablesStatus = SUCCESSSTATUS;
        state.restaurant.tables = action.payload.data;
      })
      .addCase(fetchAllTables.rejected, (state) => {
        state.fetchAllTablesStatus = FAILEDSTATUS;
      })

      // FETCHED PARCEL ORDERS
      .addCase(fetchParcelsOrder.pending, (state) => {
        state.fetchParcelsOrderStatus = LOADINGSTATUS;
      })
      .addCase(fetchParcelsOrder.fulfilled, (state, action) => {
        state.fetchParcelsOrderStatus = SUCCESSSTATUS;
        state.restaurant.parcelsOrder = action.payload.data;
      })
      .addCase(fetchParcelsOrder.rejected, (state) => {
        state.fetchParcelsOrderStatus = FAILEDSTATUS;
      })

      // SAVED ORDERS HISTORY
      .addCase(saveOrder.pending, (state) => {
        state.ordersHistoryStatus = LOADINGSTATUS;
      })
      .addCase(saveOrder.fulfilled, (state, action) => {
        state.ordersHistoryStatus = SUCCESSSTATUS;
        state.ordersHistory = action.payload.ordersDetails;
      })
      .addCase(saveOrder.rejected, (state) => {
        state.ordersHistoryStatus = FAILEDSTATUS;
      })

      // FETCHED ORDERS HISTORY
      .addCase(fetchOrders.pending, (state) => {
        state.ordersHistoryStatus = LOADINGSTATUS;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.ordersHistoryStatus = SUCCESSSTATUS;
        state.ordersHistory = action.payload.ordersDetails;
      })
      .addCase(fetchOrders.rejected, (state) => {
        state.ordersHistoryStatus = FAILEDSTATUS;
      });
  },
});

export const { clearCategoryState } = userSlice.actions;
export default userSlice.reducer;
